import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { enUS, es } from 'date-fns/locale';
import { Close, OpenInNew } from '@mui/icons-material';
import { Box, Chip, Typography } from '@mui/material';
import UserAvatar from 'components/shared/user/UserAvatar';
import SessionCardAdminView from 'components/shared/cards/session/SessionCardAdminView';
import StyledSessionDetailsDialog from 'common/theme/dialog/session-details/StyledSessionDetailsDialog';
import StyledSessionDetailsDialogButton from 'common/theme/dialog/session-details/StyledSessionDetailsDialogButton';
import StyledSessionDetailsDialogCloseButton from 'common/theme/dialog/session-details/StyledSessionDetailsDialogCloseButton';
import StyledSessionDetailsDialogTitle from 'common/theme/dialog/session-details/StyledSessionDetailsDialogTitle';
import StyledSessionCardChip from 'common/theme/cards/session/basic/StyledSessionCardChip';
import StyledSessionCardContainer from 'common/theme/cards/session/basic/StyledSessionCardContainer';
import StyledSessionCardDate from 'common/theme/cards/session/basic/StyledSessionCardDate';
import StyledSessionCardParticipants from 'common/theme/cards/session/basic/StyledSessionCardParticipants';
import { dateFormat, hourMinuteFormat, timeFormat } from 'common/constants/date';
import { roles } from 'common/constants/options';
import { convertUtcToLocalTime } from 'common/utils/date';
import { getCategoryDescription } from 'common/constants/sessionCategoryDescriptions';
import { canClientGoToSession, getTranslatedSessionStatus, isMembershipActive } from 'common/utils/session';
import { isAdmin, isClient } from 'common/utils/user';

const activePlusClient = { full_name: 'Active Plus', photo: null };

function GroupSessionDetailsDialog({ event = null, onClose, open, viewType = roles.administrator }) {
  const { t, i18n } = useTranslation(['buttons', 'labels', 'sessions']);
  const dateLocale = i18n.language === 'es' ? es : enUS;
  const membershipStatus = useSelector((state) => state.auth.user?.membership_status);

  if (!event) return null;

  const { extendedProps } = event;
  const trainer = extendedProps.trainer ?? { full_name: 'Entrenador' };
  const sessionTypeName = extendedProps.sessionTypeName ?? 'Sesión grupal';
  const status = extendedProps.status ?? 'SCHEDULED';
  const startDate = event.start;
  const endDate = event.end;
  const meetingUrl = trainer.online_meeting_url ?? null;
  const statusChip = getTranslatedSessionStatus(status, t);
  const about = getCategoryDescription(sessionTypeName, i18n.language);

  const handleGoToSession = () => {
    if (meetingUrl) {
      window.open(meetingUrl, '_blank');
      onClose();
    }
  };

  // El cliente solo puede ingresar (Zoom) si su membresía está ACTIVA (mora/inactivo lo bloquean).
  // Admin/entrenador no se gatean por membresía.
  const canGoToSession =
    canClientGoToSession(endDate, status) && (!isClient(viewType) || isMembershipActive(membershipStatus));

  return (
    <StyledSessionDetailsDialog onClose={onClose} open={open}>
      <StyledSessionDetailsDialogTitle>
        {canGoToSession && meetingUrl ? (
          <StyledSessionDetailsDialogButton
            color="secondary"
            variant="contained"
            size="small"
            endIcon={<OpenInNew />}
            disableElevation
            onClick={handleGoToSession}>
            {t('goToSession', { ns: 'buttons' })}
          </StyledSessionDetailsDialogButton>
        ) : (
          <span />
        )}
        <StyledSessionDetailsDialogCloseButton aria-label="close" onClick={onClose}>
          <Close />
        </StyledSessionDetailsDialogCloseButton>
      </StyledSessionDetailsDialogTitle>

      <StyledSessionCardContainer>
        <Box sx={{ flexGrow: 1 }}>
          {/* Distintivo dorado: estas son clases de la membresía Active Plus. */}
          <Chip
            label="Active Plus"
            size="small"
            sx={{ bgcolor: 'gold.soft', color: 'gold.main', fontWeight: 700, height: 22, mb: 1, '& .MuiChip-label': { px: 1 } }}
          />
          <StyledSessionCardDate variant="h6">
            {convertUtcToLocalTime(startDate, dateFormat, dateLocale)}
          </StyledSessionCardDate>
          <Typography variant="body2">
            {t('category', { ns: 'labels' })}: {sessionTypeName}
          </Typography>
          <Typography variant="body2">
            {t('time', { ns: 'labels' })}: {convertUtcToLocalTime(startDate, hourMinuteFormat)} -{' '}
            {convertUtcToLocalTime(endDate, timeFormat)}
          </Typography>
          <StyledSessionCardChip label={statusChip.label} color={statusChip.color} />
          {about && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, display: 'block' }}>
                {t('aboutClass', { ns: 'sessions', defaultValue: 'Sobre esta clase' })}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>{about}</Typography>
            </Box>
          )}
        </Box>
        <StyledSessionCardParticipants viewType={viewType}>
          {isAdmin(viewType) ? (
            <SessionCardAdminView
              client={activePlusClient}
              trainer={trainer}
            />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <UserAvatar size={48} textSize={20} user={trainer} />
              <Typography variant="caption">{trainer.full_name}</Typography>
            </Box>
          )}
        </StyledSessionCardParticipants>
      </StyledSessionCardContainer>

    </StyledSessionDetailsDialog>
  );
}

GroupSessionDetailsDialog.propTypes = {
  event: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  viewType: PropTypes.string
};

export default GroupSessionDetailsDialog;
